import { BRAND, SERVICES, SOCIAL_LINKS } from "./constants";

/** Canonical production URL — override via REACT_APP_SITE_URL in .env */
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://gtechnohubb.com"
).replace(/\/$/, "");

export const SITE_NAME = "Gtechnohubb";
export const DEFAULT_OG_IMAGE = BRAND.logo;

export const STATIC_PUBLIC_PATHS = [
  "/",
  "/services",
  "/about",
  "/industries",
  "/contact",
  "/book-consultation",
];

export function getAllPublicPaths() {
  return [
    ...STATIC_PUBLIC_PATHS,
    ...SERVICES.map((s) => `/services/${s.slug}`),
  ];
}

export function absoluteUrl(path = "/") {
  if (!path || path === "/") return `${SITE_URL}/`;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

export function truncate(text, max) {
  if (!text || text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
}

export function organizationSchema() {
  const hq = BRAND.offices[0];
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    legalName: hq?.line1 || SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    email: BRAND.email,
    telephone: BRAND.phone.replace(/\s/g, ""),
    description:
      "Digital transformation, AI, cloud, and full-stack IT consulting for enterprises worldwide. Based in Bangalore, India.",
    address: {
      "@type": "PostalAddress",
      streetAddress: [hq?.line2, hq?.line3].filter(Boolean).join(", "),
      addressLocality: "Bangalore",
      postalCode: "560103",
      addressCountry: "IN",
    },
    areaServed: ["IN", "US", "GB", "AE", "SG"],
    sameAs: SOCIAL_LINKS.map((s) => s.href),
  };
}

export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-IN",
  };
}

export function localBusinessSchema(office = BRAND.offices[0]) {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    image: DEFAULT_OG_IMAGE,
    url: SITE_URL,
    telephone: BRAND.phone.replace(/\s/g, ""),
    email: BRAND.email,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: [office.line2, office.line3].filter(Boolean).join(", "),
      addressLocality: "Bangalore",
      postalCode: office.line4?.match(/\d{6}/)?.[0] || "560103",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 12.9716,
      longitude: 77.5946,
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function serviceSchema(service) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.description || service.short,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: {
      "@type": "Country",
      name: "India",
    },
    url: absoluteUrl(`/services/${service.slug}`),
  };
}

export const PAGE_SEO = {
  home: {
    title: "Gtechnohubb | IT Consulting & Digital Transformation Bangalore",
    description:
      "Gtechnohubb — New Gen Technology. IT consulting, AI, cloud, web & mobile development, and healthcare IT. Bangalore HQ, global delivery since 2013.",
    path: "/",
  },
  services: {
    title: "IT Services & Software Solutions | Gtechnohubb",
    description:
      "Explore 14 practice areas: web & app development, AI, cloud, DevOps, medical coding, CRM/ERP, IT outsourcing, and digital marketing. Senior-led teams.",
    path: "/services",
  },
  about: {
    title: "About Gtechnohubb | Senior-Led IT Consulting Since 2013",
    description:
      "Meet Gtechnohubb — engineers first, consultants second. 240+ clients across 18 countries. Integrity, transparency, and outcome-obsessed delivery.",
    path: "/about",
  },
  industries: {
    title: "Industries We Serve | Healthcare, Finance, SaaS & More",
    description:
      "Industry-specific IT solutions for healthcare, finance, e-commerce, manufacturing, logistics, education, and startups. Bangalore & worldwide.",
    path: "/industries",
  },
  contact: {
    title: "Contact Gtechnohubb | IT Consulting Bangalore",
    description:
      "Start a conversation with Gtechnohubb. Email info@gtechnohubb.com or call +91 89704 12376. Free 30-minute consultation with a senior engineer.",
    path: "/contact",
  },
  bookConsultation: {
    title: "Book a Free Consultation | Gtechnohubb",
    description:
      "Schedule a free consultation with a senior Gtechnohubb engineer. No sales pitch — honest advice on your digital transformation, cloud, or AI project.",
    path: "/book-consultation",
  },
  notFound: {
    title: "Page Not Found | Gtechnohubb",
    description: "The page you requested could not be found on Gtechnohubb.",
    path: "/404",
    noindex: true,
  },
};
